import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import Button from 'kingpin/atoms/Button';
import Typography from 'kingpin/atoms/Typography';
import MarkdownBody from 'components/MarkdownBody';
import Row from 'components/Common/Row';
import TextArea from 'components/Inputs/TextArea';
import AccountPickerContext from 'contexts/AccountPickerContext';
import { AccountTodayOverrideContext } from 'contextProviders/SplashScreenProviders/UserAndAccountProviders/AccountTodayOverrideProvider';
import useMetricOptions from 'hooks/useMetricOptions';
import streamInsightsQuery, {
  AGENT_KB_TRY_IT_PATH,
  AnswerSource,
} from 'services/streamInsightsQuery';

// Ask the real agent a question against the UNPUBLISHED draft. Same model, same
// tools, same account data — only the knowledge base differs, and it is read
// from Firestore, so nothing another user's request could resolve to is
// created. This is the only honest way to know what an edit does.

const Pane = styled.div<{ isDrawer: boolean }>`
  flex: 1;
  overflow-y: auto;
  padding: ${(props) => (props.isDrawer ? '8px 0' : '24px 32px')};
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const QuestionArea = styled(TextArea)<{ isDrawer: boolean }>`
  min-height: 80px;
  width: 100%;
  max-width: ${(props) => (props.isDrawer ? '100%' : '760px')};
`;

const AnswerDiv = styled.div<{ isDrawer: boolean }>`
  border: 1px solid var(--color-border-secondary);
  border-radius: 8px;
  padding: 16px;
  max-width: ${(props) => (props.isDrawer ? '100%' : '760px')};
`;

const GroundedDiv = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 760px;
`;

const TryItPanel = ({
  variant,
  initialQuestion,
  onSaveAsTest,
}: {
  variant?: 'drawer';
  initialQuestion?: string;
  // Opens the test editor prefilled with the question and the first grounded
  // metric. Wrapping the editor in a metrics gate is the caller's job.
  onSaveAsTest?: (prefill: {
    question: string;
    metricId?: string;
    metricName?: string;
  }) => void;
}) => {
  const isDrawer = variant === 'drawer';
  const { selectedAccountId } = useContext(AccountPickerContext);
  const { todayOverride } = useContext(AccountTodayOverrideContext);
  const { metricOptionsLookup } = useMetricOptions();
  const [question, setQuestion] = useState<string>(initialQuestion ?? '');
  const [answer, setAnswer] = useState<string>('');
  const [status, setStatus] = useState<string>('');
  const [isRunning, setIsRunning] = useState<boolean>(false);
  const [sources, setSources] = useState<AnswerSource[]>([]);

  const metricName = (source: AnswerSource): string =>
    metricOptionsLookup[source.metricId]?.name ??
    source.metricName ??
    source.metricId;

  const onRun = async () => {
    setIsRunning(true);
    setAnswer('');
    setSources([]);
    setStatus('Thinking…');
    await streamInsightsQuery({
      accountId: selectedAccountId,
      question,
      history: [],
      todayOverride,
      path: AGENT_KB_TRY_IT_PATH,
      onEvent: (event) => {
        if (event.type === 'delta') {
          setAnswer((current) => current + event.text);
        } else if (event.type === 'tool') {
          setStatus(event.label ?? event.name);
        } else if (event.type === 'grounding') {
          setSources((current) => [...current, event.source]);
        } else if (event.type === 'error') {
          setStatus(event.message);
        } else if (event.type === 'done') {
          setStatus('');
        }
      },
    });
    setIsRunning(false);
  };

  const firstSource = sources[0];

  return (
    <Pane isDrawer={isDrawer} data-testid="agent-kb-try-it">
      {!isDrawer && (
        <div>
          <Typography.Header type="H4">Try the draft</Typography.Header>
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            Runs against this account&apos;s data with the unpublished knowledge
            base. Nothing is published and no version folder is written.
          </Typography.Body>
        </div>
      )}
      <QuestionArea
        isDrawer={isDrawer}
        value={question}
        onChange={(event: React.ChangeEvent<HTMLTextAreaElement>) =>
          setQuestion(event.target.value)
        }
        placeholder="What were our loaded miles per driver last month?"
        data-testid="agent-kb-try-it-question"
      />
      <Row centerAlign style={{ gap: 12 }}>
        <Button
          type="Primary"
          size="Small"
          label={isRunning ? 'Running…' : 'Ask'}
          isDisabled={isRunning || question.trim() === ''}
          onClick={onRun}
          testId="agent-kb-try-it-ask"
        />
        {status !== '' && (
          <Typography.Body type="Body 12" color="var(--color-text-secondary)">
            {status}
          </Typography.Body>
        )}
      </Row>
      {answer !== '' && (
        <AnswerDiv isDrawer={isDrawer} data-testid="agent-kb-try-it-answer">
          <MarkdownBody>{answer}</MarkdownBody>
        </AnswerDiv>
      )}
      {sources.length > 0 && (
        <GroundedDiv data-testid="agent-kb-try-it-grounded">
          <Typography.Body type="Label" color="var(--color-text-secondary)">
            Grounded on
          </Typography.Body>
          {sources.map((source) => (
            <Typography.Body key={source.metricId} type="Body 12">
              {metricName(source)}
            </Typography.Body>
          ))}
          {onSaveAsTest && (
            <Row style={{ marginTop: 8 }}>
              <Button
                type="Secondary"
                size="Small"
                label="Save as test"
                testId="agent-kb-try-it-save-as-test"
                onClick={() =>
                  onSaveAsTest({
                    question,
                    metricId: firstSource?.metricId,
                    metricName: firstSource
                      ? metricName(firstSource)
                      : undefined,
                  })
                }
              />
            </Row>
          )}
        </GroundedDiv>
      )}
    </Pane>
  );
};

export default TryItPanel;
